/**
 * FYDetail.jsx — v9
 * FIXES:
 *   - useSearchParams hook so returning from EditEntryPage/AddEntryPage
 *     correctly restores activeModule and yeaTab from URL params
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, TrendingUp, TrendingDown, ArrowLeftRight,
  FileText, BarChart2, Wallet, Tag, ChevronDown,
  Calculator, BookOpen, Sun, Moon, ArrowLeft, LogOut, ChevronRight,
} from 'lucide-react';
import { getFYs, getFY } from '../../api/yea.api';
import { useTheme } from '../../context/ThemeContext';
import { useAuth }  from '../../context/AuthContext';
import { getSociety } from '../../api/society.api';
import FYDashboard    from './FYDashboard';
import LedgerTable    from './LedgerTable';
import ContraForm     from './ContraForm';
import ReceiptPayment from './ReceiptPayment';
import SummaryReport  from './SummaryReport';
import CashBankReport from './CashBankReport';
import HeadsManager   from './HeadsManager';
import SocietyBook    from '../societybook/index';
import IEAccount      from '../ieaccount/index';
import Logo           from '../../components/common/Logo';
import '../accounting/yea.css';
import './FYDetail.css';

const YEA_TABS = [
  { key: 'dashboard', label: 'Dashboard',        Icon: LayoutDashboard },
  { key: 'income',    label: 'Income',            Icon: TrendingUp      },
  { key: 'expense',   label: 'Expense',           Icon: TrendingDown    },
  { key: 'contra',    label: 'Contra',            Icon: ArrowLeftRight  },
  { key: 'rp',        label: 'Receipt & Payment', Icon: FileText        },
  { key: 'summary',   label: 'Summary',           Icon: BarChart2       },
  { key: 'cashbank',  label: 'Cash & Bank',       Icon: Wallet          },
  { key: 'heads',     label: 'Account Heads',     Icon: Tag             },
];

// ── Module Selection Screen ───────────────────────────────────────────────────
const ModuleSelector = ({ fy, allFYs, societyName, activeModules, onSelect, onFYChange, currentFyId }) => {
  const hasYEA = activeModules.includes('year_end_accounting');
  const hasSB  = activeModules.includes('society_book');
  const hasIE  = activeModules.includes('income_expenditure');

  return (
    <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* FY selector */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>
            Financial Year
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 1rem' }}>
            <select value={currentFyId} onChange={e => onFYChange(e.target.value)}
              style={{ background: 'transparent', border: 'none', color: 'var(--gold)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer', outline: 'none' }}>
              {allFYs.map(f => <option key={f._id} value={f._id}>{f.fyName}</option>)}
            </select>
            <ChevronDown size={14} color="var(--text-dim)" />
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.35rem' }}>
            {new Date(fy.startDate).toLocaleDateString('en-IN')} — {new Date(fy.endDate).toLocaleDateString('en-IN')}
          </div>
        </div>

        {/* Module picker heading */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', marginBottom: '0.3rem' }}>{societyName}</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Select a module to open</p>
        </div>

        {/* Module cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>

          {hasYEA && (
            <button onClick={() => onSelect('yea')}
              style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', padding: '1.25rem 1.5rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'all var(--t-fast)', textAlign: 'left', width: '100%' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.background = 'var(--gold-glow)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--gold-glow)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Calculator size={20} color="var(--gold)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)', marginBottom: '0.2rem' }}>Year End Accounting</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Income, Expense, Contra, Reports, Cash & Bank</div>
              </div>
              <ChevronRight size={16} color="var(--text-dim)" />
            </button>
          )}

          {hasSB && (
            <button onClick={() => onSelect('society_book')}
              style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', padding: '1.25rem 1.5rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'all var(--t-fast)', textAlign: 'left', width: '100%' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--info)'; e.currentTarget.style.background = 'var(--info-bg)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--info-bg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BookOpen size={20} color="var(--info)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)', marginBottom: '0.2rem' }}>Society Book</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Members, Billing, Receipts, Outstanding</div>
              </div>
              <ChevronRight size={16} color="var(--text-dim)" />
            </button>
          )}

          {hasIE && (
            <button onClick={() => onSelect('ie_account')}
              style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', padding: '1.25rem 1.5rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'all var(--t-fast)', textAlign: 'left', width: '100%' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#276749'; e.currentTarget.style.background = 'rgba(39,103,73,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(39,103,73,0.1)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileText size={20} color="#276749" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)', marginBottom: '0.2rem' }}>Income & Expenditure Account</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Auto-generated · YEA & Billing · Audit ready</div>
              </div>
              <ChevronRight size={16} color="var(--text-dim)" />
            </button>
          )}

          {!hasYEA && !hasSB && !hasIE && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              No modules assigned. Contact your administrator.
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const FYDetail = () => {
  const { societyId, fyId: paramFyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, toggle: toggleTheme } = useTheme();
  const { logout } = useAuth();

  const [fy,          setFy]          = useState(null);
  const [allFYs,      setAllFYs]      = useState([]);
  const [society,     setSociety]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [currentFyId, setCurrentFyId] = useState(paramFyId);

  const [activeModule, setActiveModule] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('module') || null;
  });

  const [yeaTab, setYeaTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'dashboard';
  });

  // FIX: re-read module and tab whenever URL search params change
  // This handles returning from EditEntryPage / AddEntryPage / ImportEntryPage
  useEffect(() => {
    const mod = searchParams.get('module');
    const tab = searchParams.get('tab');
    if (mod) setActiveModule(mod);
    if (tab) setYeaTab(tab);
  }, [searchParams]);

  const fetchData = useCallback(async (fyIdToLoad) => {
    setLoading(true);
    try {
      const [fyRes, socRes, fysRes] = await Promise.all([
        getFY(societyId, fyIdToLoad),
        getSociety(societyId).catch(() => null),
        getFYs(societyId).catch(() => ({ data: { fys: [] } })),
      ]);
      setFy(fyRes.data.fy);
      if (socRes?.data?.society) setSociety(socRes.data.society);
      setAllFYs(fysRes.data.fys || fysRes.data.financialYears || []);
    } catch {
      toast.error('Financial year not found');
      navigate(`/dashboard/society/${societyId}`);
    } finally { setLoading(false); }
  }, [societyId, navigate]);

  useEffect(() => { fetchData(currentFyId); }, [fetchData, currentFyId]);

  const handleFYChange = (newFyId) => {
    setCurrentFyId(newFyId);
    setActiveModule(null);
    navigate(`/dashboard/society/${societyId}/fy/${newFyId}`, { replace: true });
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!fy)     return null;

  const banks         = fy.bankOpeningBalances || [];
  const fyName        = fy.fyName;
  const societyName   = society?.name || 'Society';
  const activeModules = society?.activeModules || [];

  return (
    <div className="yea-layout">

      {/* ── TOP NAV ── */}
      <header className="yea-nav-v2">
        <div className="yea-nav-left">
          <Logo size="sm" />
        </div>
        <div className="yea-nav-right">
          {activeModule && (
            <button onClick={() => setActiveModule(null)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '0.38rem 0.85rem', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-muted)', transition: 'all var(--t-fast)' }}>
              <ArrowLeft size={12} /> Back to Modules
            </button>
          )}
          <button onClick={() => navigate(`/dashboard/society/${societyId}`)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '0.38rem 0.85rem', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-muted)', transition: 'all var(--t-fast)' }}>
            <ArrowLeft size={12} /> Back
          </button>
          <span className="yea-society-name">{fyName}</span>
          <button onClick={toggleTheme} title="Toggle theme"
            style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <button onClick={() => { logout(); navigate('/user/login'); }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '0.38rem 0.85rem', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-muted)', transition: 'all var(--t-fast)' }}>
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      {/* ── YEA TAB BAR ── */}
      {activeModule === 'yea' && (
        <div className="yea-tab-bar">
          {YEA_TABS.map(({ key, label, Icon }) => (
            <button key={key}
              className={`yea-tab ${yeaTab === key ? 'active' : ''}`}
              onClick={() => setYeaTab(key)}>
              <Icon /> {label}
            </button>
          ))}
        </div>
      )}

      {/* ── CONTENT ── */}
      <div className="yea-content">

        {activeModule && (
          <div className="fy-selector-bar">
            <span className="fy-selector-label">Financial Year</span>
            <div className="fy-selector-wrap">
              <select className="fy-selector-select" value={currentFyId}
                onChange={e => handleFYChange(e.target.value)}>
                {allFYs.map(f => <option key={f._id} value={f._id}>{f.fyName}</option>)}
              </select>
              <ChevronDown size={13} className="fy-selector-icon" />
            </div>
            {fy.approvalStatus && fy.approvalStatus !== 'none' && (
              <span className="badge badge-warning" style={{ marginLeft: '0.5rem' }}>
                {fy.approvalStatus.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        )}

        {!activeModule && (
          <ModuleSelector
            fy={fy} allFYs={allFYs} societyName={societyName}
            activeModules={activeModules} currentFyId={currentFyId}
            onSelect={setActiveModule} onFYChange={handleFYChange}
          />
        )}

        {activeModule === 'yea' && (
          <>
            {yeaTab === 'dashboard' && <FYDashboard    societyId={societyId} fyId={currentFyId} fyName={fyName} />}
            {yeaTab === 'income'    && <LedgerTable    societyId={societyId} fyId={currentFyId} type="income"  banks={banks} fyName={fyName} societyName={societyName} fy={fy} />}
            {yeaTab === 'expense'   && <LedgerTable    societyId={societyId} fyId={currentFyId} type="expense" banks={banks} fyName={fyName} societyName={societyName} fy={fy} />}
            {yeaTab === 'contra'    && <ContraForm     societyId={societyId} fyId={currentFyId} banks={banks} />}
            {yeaTab === 'rp'        && <ReceiptPayment societyId={societyId} fyId={currentFyId} fyName={fyName} societyName={societyName} />}
            {yeaTab === 'summary'   && <SummaryReport  societyId={societyId} fyId={currentFyId} fyName={fyName} societyName={societyName} />}
            {yeaTab === 'cashbank'  && <CashBankReport societyId={societyId} fyId={currentFyId} banks={banks} fyName={fyName} societyName={societyName} />}
            {yeaTab === 'heads'     && <HeadsManager   societyId={societyId} />}
          </>
        )}

        {activeModule === 'society_book' && (
          <SocietyBook societyId={societyId} activeFY={fy} />
        )}

        {activeModule === 'ie_account' && (
          <IEAccount
            societyId={societyId}
            fyId={currentFyId}
            financialYear={fy.fyName}
          />
        )}

      </div>
    </div>
  );
};

export default FYDetail;
